import React, {useRef, useMemo, useState, useEffect} from 'react';
import {useFilter} from 'react-aria-components';
import {SelectListBox, SelectItem} from './Select';
import {Popover} from './Popover';
import {ChevronDown} from 'lucide-react';
import './MultiSelect.css';

/**
 * MultiSelect component - a searchable multi-select dropdown styled to match React Spectrum's Picker.
 *
 * Props:
 * - label: string - Label text for the field
 * - items: Array<{id, name}> - Options list (objects with id and name)
 * - selectedIds: Array - Currently selected IDs
 * - onSelectionChange: (selectedKeys: Array) => void - Called when selection changes
 * - isDisabled: boolean - Whether the dropdown is disabled
 * - disabledMessage: string - Helper text shown when disabled
 * - placeholder: string - Placeholder text when nothing is selected (default: "All")
 */
export function MultiSelect(props) {
  const {
    label,
    items = [],
    selectedIds = [],
    onSelectionChange,
    isDisabled = false,
    disabledMessage,
    placeholder = 'All'
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const triggerRef = useRef(null);
  const {contains} = useFilter({sensitivity: 'base'});

  const selectedKeys = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedCount = selectedIds.length;
  const allSelected = items.length > 0 && selectedCount === items.length;

  useEffect(() => {
    if (isDisabled && isOpen) {
      setIsOpen(false);
    }
  }, [isDisabled, isOpen]);

  const filteredItems = useMemo(() => {
    if (!searchValue) return items;
    return items.filter(item => contains(item.name, searchValue));
  }, [items, searchValue, contains]);

  const handleToggle = () => {
    if (!isDisabled) {
      const opening = !isOpen;
      setIsOpen(opening);
      if (opening) setSearchValue('');
    }
  };

  const handleSelectAll = () => {
    if (onSelectionChange) {
      onSelectionChange(items.map(item => item.id));
    }
  };

  const handleClearAll = () => {
    if (onSelectionChange) {
      onSelectionChange([]);
    }
  };

  return (
    <div className="multi-select-wrapper">
      {label && <label className="multi-select-label">{label}</label>}
      <button
        ref={triggerRef}
        className={`multi-select-trigger ${isDisabled ? 'is-disabled' : ''} ${isOpen ? 'is-open' : ''}`}
        onClick={handleToggle}
        disabled={isDisabled}
        type="button"
      >
        <span className="multi-select-value">
          {selectedCount === 0 ? placeholder : `${selectedCount} selected`}
        </span>
        <ChevronDown className="multi-select-chevron" />
      </button>
      <Popover
        triggerRef={triggerRef}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        hideArrow
        className="multi-select-popover"
      >
        <div className="multi-select-search">
          <input
            type="text"
            placeholder="Search..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            autoFocus
            className="multi-select-search-input"
          />
        </div>
        <div className="multi-select-actions">
          <button
            type="button"
            className="multi-select-action-btn"
            onClick={handleSelectAll}
            disabled={allSelected}
          >
            Select All
          </button>
          <button
            type="button"
            className="multi-select-action-btn"
            onClick={handleClearAll}
            disabled={selectedCount === 0}
          >
            Clear All
          </button>
        </div>
        <SelectListBox
          selectionMode="multiple"
          selectedKeys={selectedKeys}
          onSelectionChange={(keys) => {
            if (onSelectionChange) {
              onSelectionChange(Array.from(keys));
            }
          }}
          items={filteredItems}
          aria-label={label}
        >
          {item => <SelectItem id={item.id} textValue={item.name}>{item.name}</SelectItem>}
        </SelectListBox>
      </Popover>
      {isDisabled && disabledMessage && (
        <span className="multi-select-disabled-message">{disabledMessage}</span>
      )}
    </div>
  );
}
